import {
    IAddKeyEndpointResponse,
    IDefaultEndpointResponse,
    IGenericPayload,
    IResetKeyEndpointResponse,
    IUserUpdatePayload,
    User
} from "./types";
import {auth} from "./firebase";

const formatURL = (path: string) => `${(process.env.REACT_APP_API_BASE_URL)}${path}`
const USER_INFO_ENDPOINT = (id: string) => formatURL(`/admin/user?id=${id}`)
const BLOCK_USER_ENDPOINT = formatURL('/admin/user/block')
const UNBLOCK_USER_ENDPOINT = formatURL('/admin/user/unblock')
const UPDATE_USER_ENDPOINT = formatURL('/admin/user/update')
const ADD_KEY_ENDPOINT = formatURL('/admin/key/add')
const RESET_KEY_ENDPOINT = formatURL('/admin/key/reset')
const RESET_CREDITS_ENDPOINT = formatURL('/admin/credits/reset')
const SEND_AUTH_EMAIL_ENDPOINT = formatURL('/portal/send-auth-email')

// Asks the backend to email a passwordless sign-in link (delivered via Postmark). Public:
// the caller is not signed in yet, so no ID token is sent.
export const postSendAuthEmail = async (email: string): Promise<void> => {
    const response = await fetch(SEND_AUTH_EMAIL_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({email}),
    })

    if (!response.ok) {
        const {error} = await response.json().catch(() => ({}))
        throw new Error(error ?? 'Error sending an email with a login link')
    }
}

export const getUserInfo = async (id: string): Promise<User> => {
    const token = await auth.currentUser?.getIdToken(true);
    const response = await fetch(USER_INFO_ENDPOINT(encodeURIComponent(id)), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    })

    if (!response.ok) {
        const {errorCode} = await response.json()
        throw new Error(errorCode?.message ?? 'Error fetching user info')
    }

    return await response.json()
}

export const postBlockUser = async (activationKey: string): Promise<IDefaultEndpointResponse> => {
    const payload: IGenericPayload = {
        activation_key: activationKey
    }
    const token = await auth.currentUser?.getIdToken(true);
    const response = await fetch(BLOCK_USER_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    })

    if (!response.ok) {
        const {errorCode} = await response.json()
        throw new Error(errorCode?.message ?? 'Error blocking user')
    }

    return await response.json()
}

export const postUnblockUser = async (activationKey: string): Promise<IDefaultEndpointResponse> => {
    const payload: IGenericPayload = {
        activation_key: activationKey
    }
    const token = await auth.currentUser?.getIdToken(true);
    const response = await fetch(UNBLOCK_USER_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    })

    if (!response.ok) {
        const {errorCode} = await response.json()
        throw new Error(errorCode?.message ?? 'Error unblocking user')
    }

    return await response.json()
}

export const postAddKey = async (activationKey: string): Promise<IAddKeyEndpointResponse> => {
    const payload: IGenericPayload = {
        activation_key: activationKey
    }
    const token = await auth.currentUser?.getIdToken(true);
    const response = await fetch(ADD_KEY_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    })

    if (!response.ok) {
        const {errorCode} = await response.json()
        throw new Error(errorCode?.message ?? 'Error adding a new key')
    }

    return await response.json()
}

export const postResetKey = async (activationKey: string): Promise<IResetKeyEndpointResponse> => {
    const payload: IGenericPayload = {
        activation_key: activationKey
    }
    const token = await auth.currentUser?.getIdToken(true);
    const response = await fetch(RESET_KEY_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    })

    if (!response.ok) {
        const {errorCode} = await response.json()
        throw new Error(errorCode?.message ?? 'Error resetting a key')
    }

    return await response.json()
}

export const postResetCredits = async (activationKey: string): Promise<IDefaultEndpointResponse> => {
    const payload: IGenericPayload = {
        activation_key: activationKey
    }
    const token = await auth.currentUser?.getIdToken(true);
    const response = await fetch(RESET_CREDITS_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    })

    if (!response.ok) {
        const {errorCode} = await response.json()
        throw new Error(errorCode?.message ?? 'Error resetting credits')
    }

    return await response.json()
}

export const postUpdateUser = async (activationKey: string, email: string, billingEmail?: string): Promise<IDefaultEndpointResponse> => {
    const payload: IUserUpdatePayload = {
        activation_key: activationKey,
        email
    }

    if (billingEmail) {
        payload.billing_email = billingEmail
    }

    const token = await auth.currentUser?.getIdToken(true);
    const response = await fetch(UPDATE_USER_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    })

    if (!response.ok) {
        const {errorCode} = await response.json()
        throw new Error(errorCode?.message ?? 'Error updating user')
    }

    return await response.json()
}